import { Button, message, Input, DatePicker, Table, Select, Space } from "antd";
import React, { useEffect, useState } from "react";
import API from "../../api";
import { SearchOutlined, DownloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import CustomTable from "../../components/Table/CustomTable";
import { getUserRole } from "../../utils/auth";

// Extend Day.js with plugins
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const { Option } = Select;

export default function StockMovementReport() {
  const [stockMovements, setStockMovements] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paginationInfo, setPaginationInfo] = useState({ current: 1, pageSize: 20, total: 0 });
  const [filters, setFilters] = useState({
    sourceStore: null,
    destinationStore: null,
    type: null,
    search: null,
    startDate: null,
    endDate: null,
    financialYear: null, // New filter for financial year
  });
  const [userRole, setUserRole] = useState("");

  useEffect(() => {
    setUserRole(getUserRole());
    getStores();
  }, []);

  useEffect(() => {
    getStockMovements(paginationInfo.current, paginationInfo.pageSize, filters);
  }, [filters, paginationInfo.current, paginationInfo.pageSize]);

  const handleSearchChange = (filterName, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [filterName]: value || null,
    }));
  };

  const getStockMovements = async (page = 1, pageSize = 20, filters = {}) => {
    try {
      setLoading(true);
      const { sourceStore, destinationStore, type, search, startDate, endDate, financialYear } = filters;
      const response = await API.get("/stock-movements/report", {
        params: {
          page,
          limit: pageSize,
          sourceStoreId: sourceStore,
          destinationStoreId: destinationStore,
          type,
          search,
          startDate: startDate ? dayjs(startDate).format("YYYY-MM-DD") : null,
          endDate: endDate ? dayjs(endDate).format("YYYY-MM-DD") : null,
          financialYear: financialYear, // Add financial year to API params
        },
      });

      const movementsData = Array.isArray(response?.data?.data) ? response.data.data : [];
      setStockMovements(movementsData);

      setPaginationInfo({
        current: page,
        pageSize: pageSize,
        total: response?.data?.pagination?.total || movementsData.length,
      });
    } catch (err) {
      console.error("Error fetching stock movements:", err);
      message.error("Failed to load stock movement report.");
    } finally {
      setLoading(false);
    }
  };

  const getStores = async () => {
    try {
      const response = await API.get("/stores");
      setStores(response?.data || []);
    } catch (err) {
      console.error("Error fetching stores:", err);
      message.error("Failed to load stores.");
    }
  };

  // New function to handle download
  const handleDownload = async (movementId) => {
    try {
      const result = await API.get(`/stock-movements/invoice/${movementId?._id}`);
      if (result.status === 200) {
        const getPdf = await API.get(result.data.downloadLink, {
          responseType: "arraybuffer",
        });
        const blob = new Blob([getPdf.data], { type: "application/pdf" });
        const downloadUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = `invoice_${movementId?.invoiceNo}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(downloadUrl);
      }
      
      message.success("Invoice generated successfully.");
    } catch (err) {
      console.error("Error downloading file:", err);
      message.error("Failed to download the file.");
    }
  };

  const columns = [
    {
      title: "Index",
      key: "index",
      render: (_, __, index) =>
        paginationInfo.total -
        ((paginationInfo.current - 1) * paginationInfo.pageSize + index),
    },
    {
      title: "Invoice No.",
      key: "invoiceNo",
      render: (_, movement) => movement?.invoiceNo || "N/A",
      filterDropdown: () => (
        <Input
          placeholder="Search Invoice"
          onChange={(e) => handleSearchChange("search", e.target.value)}
          value={filters.search}
          suffix={<SearchOutlined />}
        />
      ),
      filterIcon: <SearchOutlined />,
    },
    {
      title: "Date",
      key: "transferDate",
      render: (_, movement) => dayjs(movement?.transferDate).format("DD-MM-YYYY"),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <DatePicker.RangePicker
            value={
              selectedKeys && selectedKeys.length > 0
                ? [dayjs(selectedKeys[0]), dayjs(selectedKeys[1])]
                : []
            }
            onChange={(dates) => {
              setSelectedKeys(
                dates ? [dates[0]?.format("YYYY-MM-DD"), dates[1]?.format("YYYY-MM-DD")] : []
              );
            }}
            format="YYYY-MM-DD"
          />
          <div style={{ marginTop: 8 }}>
            <Button
              type="primary"
              size="small"
              style={{ width: 90, marginRight: 8 }}
              onClick={() => {
                confirm();
                if (selectedKeys && selectedKeys.length > 1) {
                  handleSearchChange("startDate", selectedKeys[0]);
                  handleSearchChange("endDate", selectedKeys[1]);
                }
              }}
            >
              Filter
            </Button>
            <Button
              size="small"
              style={{ width: 90 }}
              onClick={() => {
                clearFilters();
                setSelectedKeys([]);
                handleSearchChange("startDate", null);
                handleSearchChange("endDate", null);
              }}
            >
              Reset
            </Button>
          </div>
        </div>
      ),
      filterIcon: () => <SearchOutlined />,
    },
    {
      title: "Source Store",
      key: "sourceStore",
      render: (_, movement) => movement?.sourceStore || "N/A",
      filterDropdown: () => (
        <Select
          placeholder="Select Source Store"
          onChange={(value) => handleSearchChange("sourceStore", value)}
          value={filters.sourceStore}
          style={{ width: 200 }}
          allowClear
        >
          {stores.map((store) => (
            <Option key={store._id} value={store._id}>
              {store.name}
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: "Destination Store",
      key: "destinationStore",
      render: (_, movement) => movement?.destinationStore || "N/A",
      filterDropdown: () => (
        <Select
          placeholder="Select Destination Store"
          onChange={(value) => handleSearchChange("destinationStore", value)}
          value={filters.destinationStore}
          style={{ width: 200 }}
          allowClear
        >
          {stores.map((store) => (
            <Option key={store._id} value={store._id}>
              {store.name}
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: "Transferrer",
      key: "transferrer",
      render: (_, movement) => (
        <div>
          <div style={{ fontWeight: "bold", fontSize: "12px" }}>
            {movement?.transferrer?.name || "N/A"}
          </div>
          <div style={{ color: "gray", fontSize: "12px" }}>
            {movement?.transferrer?.email || "---"}
          </div>
        </div>
      ),
    },
    {
      title: "Products",
      key: "products",
      render: (_, movement) => (
        <ul style={{ paddingLeft: "15px", margin: 0 }}>
          {movement?.products?.map((product, idx) => (
            <li key={idx}>
              {product.productName} ({product.productCode}) - Qty: {product.quantity}
            </li>
          ))}
        </ul>
      ),
    },
    {
      title: "Download",
      key: "download",
      render: (_, movement) => (
        <Button
          icon={<DownloadOutlined />}
          onClick={() => handleDownload(movement)}
          style={{ marginRight: "8px", color: 'green' }}
        />
      ),
    },
  ];

  return (
    <div style={{
      padding: "20px",
      background: "#fff",
      borderRadius: "8px",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
    }}>
      <div
        style={{
          fontWeight: "bold",
          fontSize: "20px",
          textAlign: "center",
          marginBottom: "10px",
          color: "#1890ff",
        }}
      >
        Stock Movement Report
      </div>
      {userRole.role === "admin" && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "5px 10px",
            backgroundColor: "#fff",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
            marginBottom: "10px",
          }}
        >
          <div style={{ fontSize: "16px", fontWeight: "500" }}>
            Filters:
          </div>
          <Space>
            <Select
              placeholder="Select Source Store"
              onChange={(value) => handleSearchChange("sourceStore", value)}
              value={filters.sourceStore}
              style={{ width: 200 }}
              allowClear
            >
              {stores.map((store) => (
                <Option key={store._id} value={store._id}>
                  {store.name}
                </Option>
              ))}
            </Select>
            <Select
              placeholder="Select Destination Store"
              onChange={(value) => handleSearchChange("destinationStore", value)}
              value={filters.destinationStore}
              style={{ width: 200 }}
              allowClear
            >
              {stores.map((store) => (
                <Option key={store._id} value={store._id}>
                  {store.name}
                </Option>
              ))}
            </Select>
            <Select
              placeholder="Select Financial Year"
              onChange={(value) => handleSearchChange("financialYear", value)}
              value={filters.financialYear}
              style={{ width: 200 }}
              allowClear
            >
              {/* Example financial years; you can fetch these from an API or hardcode */}
              <Option value="23-24">2023-2024</Option>
              <Option value="24-25">2024-2025</Option>
              <Option value="25-26">2025-2026</Option>
            </Select>
          </Space>
        </div>
      )}
      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: "8px",
          padding: "20px",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
        }}
      >
        <CustomTable
          columns={columns}
          dataSource={stockMovements}
          onPaginationChange={(pagination) => {
            setPaginationInfo({
              current: pagination.current,
              pageSize: pagination.pageSize,
            });
          }}
          loading={loading}
          style={{ padding: "1px", width: "100%" }}
          total={paginationInfo.total}
        />
      </div>
    </div>
  );
}